import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { Todo } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import { Select } from '../../components/ui';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import Badge from '../../components/ui/Badge';
import { normalizeHexColor, parseStatusColorConfig, resolveStatusTextColor } from '../../utils/statusColor';

const TODO_STATUS_COLOR_COMPLETED = '#22c55e';
const TODO_STATUS_COLOR_PENDING = '#eab308';

const renderTodoStatusBadge = (completed: boolean, className?: string) => {
  const label = completed ? 'Completed' : 'Pending';
  const color = completed ? TODO_STATUS_COLOR_COMPLETED : TODO_STATUS_COLOR_PENDING;
  const parsedConfig = parseStatusColorConfig(color);
  const resolvedColor = normalizeHexColor(parsedConfig.backgroundColor) ?? color;
  return (
    <Badge
      variant="secondary"
      className={['border rounded-md px-2 py-0.5', className].filter(Boolean).join(' ')}
      style={{
        backgroundColor: resolvedColor,
        borderColor: resolvedColor,
        color: resolveStatusTextColor(resolvedColor, parsedConfig.textColorMode),
      }}
    >
      {label}
    </Badge>
  );
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Todos',
    description: 'Manage your todos database',
    i18nKey: 'crm-todos',
    permissionModuleKey: 'todos',
    route: {
      list: '/todos_monthly_view',
      new: '/todos/new',
      detail: (id: number) => `/todos/${id}`,
      edit: (id: number) => `/todos/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-todos',
      detail: (id: number) => `/crm-todos/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search todos...',
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'todoDescr', 'todoCompleted', 'todoRecurring', 'clientId', 'todoCreatedAt', 'todoUpdatedAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'todoDescr',
        label: 'Description',
        placeholder: 'All Description',
        getValue: (item: Todo) => item.todoDescr,
        options: [],
      },
      {
        key: 'todoCompleted',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: Todo) => item.todoCompleted ? 'Completed' : 'Pending',
        options: ['Completed', 'Pending'],
      },
      {
        key: 'todoRecurring',
        label: 'Recurrence',
        placeholder: 'All',
        type: 'boolean' as const,
        getValue: (item: Todo) => (item.todoRecurring ? 'Recurring' : 'One-time'),
        options: ['Recurring', 'One-time'],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All organizations',
        type: 'select' as const,
        getValue: (item: Todo) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'todoCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Todo) =>
          item.todoCreatedAt
            ? formatDateGlobal(item.todoCreatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'todoUpdatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: Todo) =>
          item.todoUpdatedAt
            ? formatDateGlobal(item.todoUpdatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Todo[],
      filters: {
        todoDescr: string[] | string;
        todoCompleted: string[];
        todoRecurring: string[];
        clientId: string[];
        todoCreatedAt: string[];
        todoUpdatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Description filter
        const descrFilter = filters.todoDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.todoDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.todoDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Completed status filter
        if (filters.todoCompleted && filters.todoCompleted.length > 0) {
          const status = item.todoCompleted ? 'Completed' : 'Pending';
          if (!filters.todoCompleted.includes(status)) return false;
        }

        // Recurrence filter
        if (filters.todoRecurring && filters.todoRecurring.length > 0) {
          const status = item.todoRecurring ? 'Recurring' : 'One-time';
          if (!filters.todoRecurring.includes(status)) return false;
        }

        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Created At filter
        if (filters.todoCreatedAt && filters.todoCreatedAt.length > 0) {
          if (!item.todoCreatedAt) {
            return false;
          }
          
          const itemDate = new Date(item.todoCreatedAt);
          const itemDateUTC = new Date(Date.UTC(
            itemDate.getUTCFullYear(),
            itemDate.getUTCMonth(),
            itemDate.getUTCDate()
          ));
          
          // Priority 1: Use dateRanges if available (from presets or custom ranges)
          if (ranges.todoCreatedAt && (ranges.todoCreatedAt.start || ranges.todoCreatedAt.end)) {
            const range = ranges.todoCreatedAt;
            
            if (range.start) {
              const startDateUTC = new Date(Date.UTC(
                range.start.getUTCFullYear(),
                range.start.getUTCMonth(),
                range.start.getUTCDate()
              ));
              if (itemDateUTC < startDateUTC) {
                return false;
              }
            }
            
            if (range.end) {
              const endDateUTC = new Date(Date.UTC(
                range.end.getUTCFullYear(),
                range.end.getUTCMonth(),
                range.end.getUTCDate(),
                23, 59, 59, 999
              ));
              if (itemDateUTC > endDateUTC) {
                return false;
              }
            }
          } else {
            // Priority 2: Fallback to string matching for preset values or direct date strings
            const value = formatDateGlobal(item.todoCreatedAt);
            const matches = filters.todoCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) {
              return false;
            }
          }
        }

        // Updated At filter
        if (filters.todoUpdatedAt && filters.todoUpdatedAt.length > 0) {
          if (!item.todoUpdatedAt) {
            return false;
          }
          
          const itemDate = new Date(item.todoUpdatedAt);
          const itemDateUTC = new Date(Date.UTC(
            itemDate.getUTCFullYear(),
            itemDate.getUTCMonth(),
            itemDate.getUTCDate()
          ));
          
          // Priority 1: Use dateRanges if available (from presets or custom ranges)
          if (ranges.todoUpdatedAt && (ranges.todoUpdatedAt.start || ranges.todoUpdatedAt.end)) {
            const range = ranges.todoUpdatedAt;
            
            if (range.start) {
              const startDateUTC = new Date(Date.UTC(
                range.start.getUTCFullYear(),
                range.start.getUTCMonth(),
                range.start.getUTCDate()
              ));
              if (itemDateUTC < startDateUTC) {
                return false;
              }
            }
            
            if (range.end) {
              const endDateUTC = new Date(Date.UTC(
                range.end.getUTCFullYear(),
                range.end.getUTCMonth(),
                range.end.getUTCDate(),
                23, 59, 59, 999
              ));
              if (itemDateUTC > endDateUTC) {
                return false;
              }
            }
          } else {
            // Priority 2: Fallback to string matching for preset values or direct date strings
            const value = formatDateGlobal(item.todoUpdatedAt);
            const matches = filters.todoUpdatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) {
              return false;
            }
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Todo',
        showTitle: false,
        variant: 'primary' as const,
        href: '/todos/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Todo) => {
      window.location.href = `/todos/${item.todoId}`;
    },
    keyExtractor: (item: Todo) => item.todoId,
    getClientId: (item: Todo) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new todo.',
    noSearchResults: 'No todos found matching your search criteria.',
    noFilterResults: 'No todos match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Todo',
    message: 'Are you sure you want to delete this todo? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete todo. Please try again.',
    handler: async (id: number) => {
      const { deleteTodo } = await import('../../services/mutations');
      await deleteTodo(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: Todo) => item.todoId,
    getTitle: (item: Todo) => item.todoDescr || `Todo #${item.todoId}`,
    getSubtitle: (item: Todo) => {
      if (item.client?.clientName) {
        return `Client: ${item.client.clientName}`;
      }
      return '';
    },
    getStatusBadge: (item: Todo) => ({
      label: item.todoCompleted ? 'Completed' : 'Pending',
      variant: (item.todoCompleted ? 'accent' : 'warning') as 'default' | 'success' | 'warning' | 'danger' | 'info',
    }),
    getCardFields: (item: Todo) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Recurrence
      if (item.todoRecurring && item.todoRecurrenceInterval) {
        const day = item.todoRecurrenceDayOfMonth != null ? ` (day ${item.todoRecurrenceDayOfMonth})` : '';
        fields.push({
          label: 'Recurrence',
          value: `${item.todoRecurrenceInterval.tri_descr}${day}`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ),
        });
      }

      // Created Date
      if (item.todoCreatedAt) {
        fields.push({
          label: 'Created',
          value: formatDateGlobal(item.todoCreatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Updated Date
      if (item.todoUpdatedAt) {
        fields.push({
          label: 'Updated',
          value: formatDateGlobal(item.todoUpdatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Todos' },
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
      ],
      getStatus: (item: Todo) => (item.todoCompleted ? 'completed' : 'pending'),
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateTodo } = await import('../../services/mutations');
      await updateTodo(id, { todoCompleted: newStatus === 'completed' });
    },
    renderStatusSelector: (item: Todo, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      return (
        <Select
          value={item.todoCompleted ? 'completed' : 'pending'}
          onChange={(e: any) => onStatusChange(e.target.value)}
          disabled={isUpdating}
          className="text-xs"
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'completed', label: 'Completed' },
          ]}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
      delete: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Todo Details',
    notFoundTitle: 'Todo not found',
    notFoundMessage: 'The todo you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Todos',
    defaultTitle: 'Unnamed Todo',
    getTitle: (item: Todo) => {
      return item.todoDescr || `Todo #${item.todoId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Todo',
      subtitle: 'Add a new todo to your database',
      submitText: 'Create Todo',
    },
    edit: {
      title: 'Edit Todo',
      submitText: 'Update Todo',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading todo...',
    errorMessage: 'Failed to save todo. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Todo>, files?: File[], _filesToDetach?: number[]) => {
      const { createTodo } = await import('../../services/mutations');
      return await createTodo(data, files);
    },
    update: async (id: number, data: Partial<Todo>, files?: File[], filesToDetach?: number[]) => {
      const { updateTodo } = await import('../../services/mutations');
      return await updateTodo(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) => `/crm-todos/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'todoDescr',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter todo description',
      rows: 4,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 1000,
      },
    },
    {
      name: 'todoCompleted',
      label: 'Completed',
      type: 'switch',
      switchLabel: 'Mark as completed',
      description: 'Check if this todo is completed',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client (optional)',
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
    },
    {
      name: 'todoTypeId',
      label: 'Type',
      type: 'select',
      placeholder: 'Select type (optional)',
      options: [],
      apiEndpoint: '/p-crm-todo-type',
      labelField: 'tt_descr',
      description: 'Call, Reminder Call, Task, Invoice, Offer, etc.',
    },
    {
      name: 'todoRecurring',
      label: 'Recurring',
      type: 'switch',
      switchLabel: 'Repeat this todo on a schedule',
      description: 'Enable to set a recurrence interval (monthly, quarterly, etc.)',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'todoRecurrenceIntervalId',
      label: 'Repeat every',
      type: 'select',
      placeholder: 'Select interval',
      apiEndpoint: '/p-crm-todo-recurrence-interval',
      labelField: 'tri_descr',
      description: 'Required when recurring is enabled',
    },
    {
      name: 'todoRecurrenceDayOfMonth',
      label: 'Day of month',
      type: 'number',
      placeholder: '1–31',
      min: 1,
      max: 31,
      description: 'Day of the month when it recurs (1–31). Leave empty to use creation day.',
    },
    {
      name: 'todoDueDate',
      label: 'Due Date',
      type: 'datetime',
      placeholder: 'Select due date',
      description: 'Optional. Non-recurring todos with a due date will appear on this date in the monthly calendar.',
    },
  ] as FieldConfig[],

  // Form Layout Configuration
  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Todo Information',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['todoDescr', 'todoCompleted', 'clientId', 'todoTypeId', 'todoDueDate'],
        fieldSpans: {
          todoDescr: 'md:col-span-12',
          todoCompleted: 'md:col-span-6',
          clientId: 'md:col-span-6',
          todoTypeId: 'md:col-span-6',
          todoDueDate: 'md:col-span-6',
        },
        collapsible: false,
      },
      {
        id: 'recurrence',
        title: 'Recurrence',
        icon: '🔄',
        gridCols: 'md:grid-cols-2',
        fields: ['todoRecurring', 'todoRecurrenceIntervalId', 'todoRecurrenceDayOfMonth'],
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<Todo>[] => [
  {
    key: 'todoDescr',
    header: 'Description',
    sortable: true,
    render: (value, row) => {
      const text = value || '-';
      const hasAttachments = (row.FileAttachment?.length ?? 0) > 0;
      const attachmentCount = row.FileAttachment?.length ?? 0;
      const clientLine = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : null);
      const content = (
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="truncate">{text}</span>
          {hasAttachments && (
            <span
              title={`${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`}
              className="shrink-0 inline-flex text-gray-500 dark:text-gray-400"
              aria-hidden
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </span>
          )}
        </span>
      );
      if (!clientLine) return content;
      return (
        <div className="flex flex-col min-w-0">
          {content}
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{clientLine}</span>
        </div>
      );
    },
  },
  {
    key: 'todoCompleted',
    header: 'Status',
    sortable: true,
    render: (_value, row) => renderTodoStatusBadge(row.todoCompleted),
  },
  {
    key: 'todoTypeId',
    header: 'Type',
    sortable: true,
    render: (value, row) => (row.todoType?.tt_descr || (value ? `Type #${value}` : '-')),
  },
  {
    key: 'todoRecurring',
    header: 'Recurrence',
    sortable: true,
    render: (_, row) => {
      if (!row.todoRecurring) return '-';
      const interval = row.todoRecurrenceInterval?.tri_descr;
      const day = row.todoRecurrenceDayOfMonth;
      if (interval && day != null) return `${interval} (day ${day})`;
      if (interval) return interval;
      return 'Yes';
    },
  },
  {
    key: 'todoDueDate',
    header: 'Due Date',
    sortable: true,
    render: (value) => (value ? formatDateTime(value) : '-'),
  },
  {
    key: 'todoCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'todoUpdatedAt',
    header: 'Updated At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Todo,
  formatDate: (dateString?: string) => string
) => {
  const sections: Array<{ title: string; fields: Array<{ label: string; value: React.ReactNode; condition?: boolean }> }> = [
    {
      title: 'Todo Information',
      fields: [
        {
          label: 'Description',
          value: item.todoDescr || '-',
        },
        {
          label: 'Status',
          value: renderTodoStatusBadge(item.todoCompleted),
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '-'),
          condition: !!item.clientId,
        },
        {
          label: 'Type',
          value: item.todoType?.tt_descr || (item.todoTypeId ? `Type #${item.todoTypeId}` : '-'),
          condition: !!item.todoTypeId,
        },
        {
          label: 'Recurring',
          value: item.todoRecurring ? 'Yes' : 'No',
        },
        {
          label: 'Recurrence interval',
          value: item.todoRecurrenceInterval?.tri_descr || '-',
          condition: !!item.todoRecurring && !!item.todoRecurrenceIntervalId,
        },
        {
          label: 'Day of month',
          value: item.todoRecurrenceDayOfMonth != null ? String(item.todoRecurrenceDayOfMonth) : '-',
          condition: !!item.todoRecurring,
        },
        {
          label: 'Due Date',
          value: item.todoDueDate ? formatDateTime(item.todoDueDate) : '-',
          condition: !!item.todoDueDate,
        },
        {
          label: 'Created At',
          value: formatDate(item.todoCreatedAt),
          condition: !!item.todoCreatedAt,
        },
        {
          label: 'Updated At',
          value: formatDate(item.todoUpdatedAt),
          condition: !!item.todoUpdatedAt,
        },
      ],
    },
  ];
  sections.push({
    title: 'Attachments',
    fields: [
      {
        label: '',
        value: React.createElement(EntityAttachmentsSection, {
          downloadEndpoint: `${moduleConfig.module.api.detail(item.todoId)}/files`,
          files: item.FileAttachment || [],
          defaultDownloadName: `todo_${item.todoId}_files.zip`,
          getViewFileUrl: (fileId) => `${moduleConfig.module.api.detail(item.todoId)}/files/${fileId}`,
        }),
      },
    ],
  });
  return sections;
};

// Helper function to get status badges
export const getStatusBadges = (item: Todo) => {
  return renderTodoStatusBadge(item.todoCompleted, 'px-3 py-1 text-sm font-semibold');
};

// Helper function to get form sections (legacy, use formLayout instead)
export const getFormSections = () => {
  return [];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'list':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Todo,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.todoId)}
        className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.todoId)}
          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.todoId);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Helper function to get initial form data
export const getInitialFormData = (): Partial<Todo> => {
  return {
    todoDescr: '',
    todoCompleted: false,
    clientId: undefined,
    todoTypeId: undefined,
    todoRecurring: false,
    todoRecurrenceIntervalId: undefined,
    todoRecurrenceDayOfMonth: undefined,
    todoDueDate: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Todo): Partial<Todo> => {
  return {
    todoDescr: item.todoDescr || '',
    todoCompleted: item.todoCompleted ?? false,
    clientId: item.clientId ?? undefined,
    todoTypeId: item.todoTypeId ?? undefined,
    todoRecurring: item.todoRecurring ?? false,
    todoRecurrenceIntervalId: item.todoRecurrenceIntervalId ?? undefined,
    todoRecurrenceDayOfMonth: item.todoRecurrenceDayOfMonth ?? undefined,
    todoDueDate: item.todoDueDate ?? undefined,
  };
};

export type ModuleConfig = typeof moduleConfig;
